// Authentication APIs (login, logout, register)

import axiosInstance from '../config/axios.config';
import ENDPOINTS from '../config/endpoints';
import tokenManager from '../utils/tokenManager';
import apiHelpers from '../utils/apiHelpers';
import { HTTP_STATUS } from '../../constants/apiConstants';

class AuthService {
  // Login - for both admin and user
  async login(credentials) {
    try {
      const response = await axiosInstance.post(ENDPOINTS.AUTH.LOGIN, credentials);
      
      const { status, message, data, token } = response.data;
      
      if (status && token) {
        // Use role from data first, then credentials
        const userRole = data.role || credentials.role;
        const space = userRole === 'admin' ? 'admin' : 'user';
        
        // Store token
        tokenManager.setAccessToken(token, space);
        
        // Store user data
        tokenManager.setUserData(data, space);
        
        // Dispatch event to notify app of login
        window.dispatchEvent(new Event('roleUpdated'));
        
        return {
          success: true,
          message,
          user: data,
          token,
        };
      }
      // Backend responded with status=false but HTTP 200. Create an error with backend message.
      const err = new Error(message || 'Login failed');
      err.response = {
        status: HTTP_STATUS.UNAUTHORIZED,
        data: { message: message || 'Login failed', errors: null },
      };
      throw err;
    } catch (error) {
      throw error;
    }
  }

  // Register new user
  async register(userData) {
    try {
      const response = await axiosInstance.post(ENDPOINTS.AUTH.REGISTER, userData);
      
      const { status, message, data, token } = response.data;
      
      // If status is true, it's a success
      if (status) {
        // We DO NOT store the token or user data here because 
        // the user must manually login after registration.
        
        return {
          success: true,
          message: message || 'Registration successful',
          user: data,
          token,
        };
      }
      // Backend responded with status=false but HTTP 200. Create an error with backend message.
      const err = new Error(message || 'Registration failed');
      err.response = {
        status: HTTP_STATUS.BAD_REQUEST,
        data: { message: message || 'Registration failed', errors: null },
      };
      throw err;
    } catch (error) {
      throw error;
    }
  }

  // Logout - call backend with user UUID, then clear tokens
  async logout() {
    try {
      const userData = tokenManager.getUserData();
      const uuid = userData?.uuid || userData?.id;
      if (uuid) {
        await axiosInstance.post(`/auth/logout/${uuid}`);
      }
    } catch (error) {
      // Swallow backend error to ensure client logout still proceeds
    }
    tokenManager.clearTokens();
    window.dispatchEvent(new Event('roleUpdated'));
    return { success: true, message: 'Logged out successfully' };
  }

  // Get current user data
  getCurrentUser() {
    return tokenManager.getUserData();
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = tokenManager.getAccessToken();
    if (!token) return false;
    
    // Check if token is expired
    return !tokenManager.isTokenExpired(token);
  }

  // Get user role
  getUserRole() {
    const userData = tokenManager.getUserData();
    return userData?.role || null;
  }

  // Check if user is admin
  isAdmin() {
    return this.getUserRole() === 'admin';
  }

  // Check if user is regular user
  isUser() {
    return this.getUserRole() === 'user';
  }

  // Forgot password - send OTP to email
  async forgotPassword(email) {
    try {
      const response = await axiosInstance.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Verify OTP
  async verifyOtp(email, otp) {
    try {
      const response = await axiosInstance.post(ENDPOINTS.AUTH.VERIFY_OTP, { email, otp });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Reset password
  async resetPassword(email, newPassword) {
    try {
      const response = await axiosInstance.post(ENDPOINTS.AUTH.RESET_PASSWORD, { email, newPassword });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Change password for a logged-in user
  async changePassword(userUuid, oldPassword, newPassword) {
    try {
      const response = await axiosInstance.put(`/auth/change/password/${userUuid}`, {
        old_password: oldPassword,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
